import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { Bot, ChevronDown, ChevronRight, ExternalLink, Image as ImageIcon, Search, X } from 'lucide-react'
import { CHECK_LEVELS, type LevelsSetResult } from '@one-deployment/api'
import type { BatchStarted, CheckLevel, ProjectAttention, ReadyTicket, TicketStory, TriageBrief, Visual, WorkspaceSummary } from '@one-deployment/api'
import { Button, Input, SkeletonRows, Textarea } from '@/components/ui'
import { Listbox } from '@/components/ui/Listbox'
import { Button as SButton } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Age, EmptyState, ErrorState, StatusDot } from '@/components/status'
import { useCan } from '@/components/RoleGate'
import { ask } from '@/components/ask'
import { enc, get, post, put } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'
import { useLiveQueryOpts } from '@/lib/live'
import { isPrinting, useBoard } from '@/features/shell/useOverview'
import { byPriority, groupInbox, SECTIONS, visibleSections } from './inboxGroups'
import { useVisuals, VisualGallery } from '@/features/visuals/Visuals'
import { Changes } from '@/features/visuals/Changes'
import { prUrl as prLink, useProject } from './data'

type Needs = ProjectAttention['needsYou'][number]

function useAttention(project: string) {
  return useQuery({
    queryKey: ['project', project, 'attention'],
    queryFn: ({ signal }) => get<ProjectAttention>(`/api/projects/${enc(project)}/attention`, signal),
    refetchInterval: 30_000,
  })
}

function useStory(id: string) {
  const live = useLiveQueryOpts(`workspace:${id}`)
  return useQuery({ queryKey: ['ws', id, 'story'], queryFn: ({ signal }) => get<TicketStory>(`/api/workspaces/${enc(id)}/story`, signal), ...live })
}

/** Reply, Try again and Start. Every success re-reads the board. */
function useActions(project: string) {
  const qc = useQueryClient()
  const done = () => {
    void qc.invalidateQueries({ queryKey: qk.board(project) })
    void qc.invalidateQueries({ queryKey: ['project', project, 'attention'] })
    void qc.invalidateQueries({ queryKey: ['ws'] })
    void qc.invalidateQueries({ queryKey: ['workspace', project] })
    void qc.invalidateQueries({ queryKey: qk.home() })
  }
  return {
    reply: useMutation({ mutationFn: (v: { workspaceId: string; text: string; picked?: boolean }) => post(`/api/workspaces/${enc(v.workspaceId)}/jobs`, { mode: 'reply', text: v.text, ...(v.picked ? { picked: true } : {}) }), onSuccess: done }),
    retry: useMutation({ mutationFn: (jobId: string) => post(`/api/jobs/${enc(jobId)}/retry`), onSuccess: done }),
    start: useMutation({ mutationFn: (identifiers: string[]) => post<BatchStarted>(`/api/projects/${enc(project)}/issues/start`, { identifiers }), onSettled: done }),
  }
}

/**
 * Automation tab as an inbox of small cards: what needs you first, then what
 * is running, then what can start; done and declined fold away. Empty
 * sections are left out. A click opens the ticket in a side panel.
 */
export function Inbox({ project }: { project: string }) {
  const board = useBoard(project)
  const attention = useAttention(project)
  const all = board.data?.workspaces
  const incoming = board.data?.incomingState ?? null
  const [filter, setFilter] = useState<Filter>(NO_FILTER)
  const groups = useMemo(() => {
    const g = groupInbox(all ?? [], incoming)
    return Object.fromEntries(Object.entries(g).map(([k, v]) => [k, v.filter((w) => matches(filter, w.ticket, w.title, w.triage))])) as typeof g
  }, [all, incoming, filter])
  const allReady = board.data?.readyTickets
  const readyTickets = byPriority(board.data?.readyTickets ?? []).filter((t) => matches(filter, t.ticket, t.title, t.triage))
  const areas = useMemo(() => [...new Set([...(all ?? []).map((w) => w.triage?.area), ...(allReady ?? []).map((t) => t.triage?.area)].filter(Boolean).map((a) => areaKey(a!)))].sort(), [all, allReady])
  const needs = useMemo(() => new Map((attention.data?.needsYou ?? []).map((n) => [n.workspaceId, n])), [attention.data])
  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(SECTIONS.map((s) => [s.key, !s.folded])))
  const linked = (useSearch({ strict: false }) as { ticket?: string }).ticket
  const [picked, setPicked] = useState<string | null>(linked ?? null)
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const anchor = useRef<{ ticket: string; on: boolean } | null>(null)
  const canStart = useCan('job.delegate', project)
  const act = useActions(project)

  if (board.error && !all) return <ErrorState error={board.error} retry={() => void board.refetch()} />
  if (!all) return <div className="p-4"><SkeletonRows rows={10} /></div>
  if (all.length === 0 && (board.data?.readyTickets ?? []).length === 0) {
    return <EmptyState icon={<Bot size={20} />} title="No tickets yet"
      body="Delegate a Linear issue to the agent (or move one into the trigger status) and it shows here within seconds." />
  }
  // Shift-click ticks every ticket between the last one clicked and this one, inclusive.
  const order = [...groups.ready.map((w) => w.ticket), ...readyTickets.map((t) => t.ticket)]
  const toggle = (t: string, shift: boolean) => {
    // Shift-click copies what the last click did: ticked ticks the range, unticked unticks it.
    const from = shift && anchor.current ? order.indexOf(anchor.current.ticket) : -1
    const to = order.indexOf(t)
    const on = from >= 0 && to >= 0 ? anchor.current!.on : !chosen.has(t)
    const span = from >= 0 && to >= 0 ? order.slice(Math.min(from, to), Math.max(from, to) + 1) : [t]
    setChosen((s) => {
      const n = new Set(s)
      for (const x of span) { if (on) n.add(x); else n.delete(x) }
      return n
    })
    anchor.current = { ticket: t, on }
  }
  const selected = all.find((w) => w.ticket === picked)

  return (
    <div className="relative flex h-full">
      <div className="min-w-0 flex-1 space-y-2 overflow-auto p-3">
        <FilterBar f={filter} set={setFilter} areas={areas} />
        {visibleSections(groups, readyTickets.length).length === 0 && <p className="px-2 py-6 text-sm text-fg-faint">No tickets match.</p>}
        {visibleSections(groups, readyTickets.length).map((s) => {
          const rows = groups[s.key]
          const count = rows.length + (s.key === 'ready' ? readyTickets.length : 0)
          const isOpen = s.key === 'needs' || open[s.key]
          return (
            <section key={s.key} aria-label={s.label}>
              <div className="flex items-center gap-2">
                <SButton variant="ghost" disabled={s.key === 'needs'} onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))} aria-expanded={isOpen}
                  className="h-9 flex-1 justify-start gap-1.5 rounded-sm px-2 text-xs font-semibold text-fg-muted hover:bg-hover hover:text-fg disabled:opacity-100">
                  {s.key !== 'needs' && (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                  {s.label}<span className="font-normal tabular-nums text-fg-faint">{count}</span>
                </SButton>
                {s.key === 'ready' && isOpen && canStart && chosen.size > 0 && (
                  <Button variant="primary" disabled={act.start.isPending} onClick={() => act.start.mutate([...chosen], { onSuccess: () => setChosen(new Set()) })}>
                    Start selected · {chosen.size}
                  </Button>
                )}
              </div>
              {s.key === 'ready' && act.start.error && <div className="px-2 py-1"><ErrorState error={act.start.error} compact /></div>}
              {isOpen && (
                <ul className="grid grid-cols-1 gap-2 px-1 pt-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {rows.map((w) => (
                    <Card key={w.id} w={w} on={w.ticket === picked} onOpen={() => setPicked((p) => (p === w.ticket ? null : w.ticket))}>
                      {s.key === 'needs' && <NeedsBits project={project} w={w} n={needs.get(w.id)} />}
                      {s.key === 'ready' && canStart && (
                        <div className="mt-2 flex items-center gap-2">
                          <Checkbox aria-label={`Pick ${w.ticket}`} className="cursor-pointer" checked={chosen.has(w.ticket)} onClick={(e) => toggle(w.ticket, e.shiftKey)} />
                          <Button className="ml-auto h-6 px-2" disabled={act.start.isPending} onClick={() => act.start.mutate([w.ticket])}>Start</Button>
                        </div>
                      )}
                    </Card>
                  ))}
                  {s.key === 'ready' && readyTickets.map((t) => (
                    <NewCard key={t.ticket} t={t}>
                      {canStart && (
                        <div className="mt-2 flex items-center gap-2">
                          <Checkbox aria-label={`Pick ${t.ticket}`} className="cursor-pointer" checked={chosen.has(t.ticket)} onClick={(e) => toggle(t.ticket, e.shiftKey)} />
                          <Button className="ml-auto h-6 px-2" disabled={act.start.isPending} onClick={() => act.start.mutate([t.ticket])}>Start</Button>
                        </div>
                      )}
                    </NewCard>
                  ))}
                </ul>
              )}
            </section>
          )
        })}
      </div>
      {selected && <Panel key={selected.id} project={project} w={selected} n={needs.get(selected.id)} onClose={() => setPicked(null)} />}
    </div>
  )
}

function Card({ w, on, onOpen, children }: { w: WorkspaceSummary; on: boolean; onOpen: () => void; children?: React.ReactNode }) {
  return (
    <li role="button" tabIndex={0} onClick={(e) => { if (!onControl(e.target)) onOpen() }}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen() } }}
      className={cn('flex min-h-32 cursor-pointer flex-col rounded-md bg-surface-2 p-2.5 hover:bg-hover focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none', on && 'ring-1 ring-accent')}>
      <div>
        <div className="flex items-center gap-2 text-xs">
          <StatusDot stage={w.stage} printing={isPrinting(w)} />
          <span className="font-mono text-fg-subtle">{w.ticket}</span>
          <SizeTag t={w.triage} />
          <Age from={w.updatedAt} className="ml-auto text-fg-faint" />
        </div>
        <div className="mt-1 truncate text-sm">{w.title}</div>
        <TriageTags t={w.triage} />
      </div>
      {children && <div className="mt-auto" onKeyDown={(e) => e.stopPropagation()}>{children}</div>}
    </li>
  )
}

const LEVEL: Record<CheckLevel, string> = {
  logic: 'Logic', design: 'Design', flow: 'Flow', text: 'Text', data: 'Data', access: 'Access', api: 'API', integration: 'Integration',
  performance: 'Speed', dependency: 'Packages', responsive: 'Responsive', accessibility: 'A11y', security: 'Security', money: 'Money',
  dates: 'Dates', notifications: 'Messages', reports: 'Reports', bug_fix: 'Bug fix',
}
const KIND: Record<TriageBrief['kind'], string> = { bug: 'Bug', feature: 'Feature', change_request: 'Change request', feedback: 'Feedback', error: 'Error', chore: 'Chore' }
// Yellow → orange → dark orange, for size and for the hour estimate.
const YELLOW = [234, 179, 8], DARK_ORANGE = [194, 65, 12]
const SIZE_COLOR = { S: 'rgb(234 179 8)', M: 'rgb(249 115 22)', L: 'rgb(194 65 12)' } as const
function heat(hours: number) {
  const k = Math.min(1, Math.log2(1 + hours) / Math.log2(1 + 8))
  return `rgb(${YELLOW.map((c, i) => Math.round(c + (DARK_ORANGE[i]! - c) * k)).join(' ')})`
}
const duration = (h: number) => (h < 1 ? `${Math.round(h * 60)}m` : `${h}h`)
/** Areas come in mixed spellings ("Time Clock", "time-clock"): one key for all. */
const areaKey = (a: string) => a.trim().toLowerCase().replace(/[\s_-]+/g, ' ')

/** The size letter by the ticket number: S yellow, M orange, L dark orange. */
function SizeTag({ t }: { t?: TriageBrief | null }) {
  if (!t) return null
  const hard = t.difficulty === 'very_hard' ? 'Needs access or keys we lack: the project lead takes it' : t.difficulty === 'easy' ? 'Easy: its pull request merges once checks pass' : null
  return (
    <>
      <span className="rounded-sm bg-surface-3 px-1.5 py-px text-[11px] font-semibold" style={{ color: SIZE_COLOR[t.size] }}>{t.size}</span>
      {hard && <span title={hard} className="rounded-sm bg-surface-3 px-1.5 py-px text-[11px] font-semibold text-fg-muted">{t.difficulty === 'easy' ? 'easy' : 'very hard'}</span>}
    </>
  )
}

/**
 * The ticket's check levels: picked by Claude after the code is written, or
 * set by hand here. Saving on an open pull request runs the checks again.
 */
function LevelsEditor({ project, w }: { project: string; w: WorkspaceSummary }) {
  const qc = useQueryClient()
  const canEdit = useCan('job.delegate', project)
  const t = w.triage
  const [draft, setDraft] = useState<CheckLevel[] | null>(null)
  const save = useMutation({
    mutationFn: (levels: CheckLevel[] | null) => put<LevelsSetResult>(`/api/projects/${enc(project)}/issues/${enc(w.ticket)}/levels`, { levels }),
    onSuccess: () => {
      setDraft(null)
      for (const queryKey of [qk.board(project), ['project', project, 'attention'], ['ws'], ['workspace', project]]) void qc.invalidateQueries({ queryKey })
    },
  })
  if (!t) return null
  const shown = t.levels ?? []
  const editing = draft !== null
  const toggle = (l: CheckLevel) => setDraft((d) => (d ?? []).includes(l) ? (d ?? []).filter((x) => x !== l) : [...(d ?? []), l])
  const chip = 'rounded-sm px-1.5 py-0.5 text-[11px]'
  return (
    <section>
      <div className="mb-1.5 flex items-center gap-2 text-xs">
        <h3 className="font-semibold text-fg-subtle">Check levels</h3>
        <span className="text-fg-faint">{t.levelsByPerson ? 'set by hand' : shown.length ? 'picked by Claude from the change' : 'Claude picks them once the code is written'}</span>
        <span className="flex-1" />
        {canEdit && !editing && <SButton variant="link" onClick={() => setDraft(shown)} className="h-auto p-0 text-xs font-normal text-accent">Edit</SButton>}
      </div>
      {!editing ? (
        <div className="flex flex-wrap gap-1">
          {shown.length ? shown.map((l) => <span key={l} className={cn(chip, 'bg-surface-3 text-fg-muted')}>{LEVEL[l]}</span>) : <span className="text-xs text-fg-faint">Only the always-run checks (build, lint, typecheck, unit tests).</span>}
        </div>
      ) : (
        <div className="rounded-md border border-border p-2">
          <div className="flex flex-wrap gap-1">
            {CHECK_LEVELS.map((l) => {
              const on = draft.includes(l)
              return <SButton key={l} variant="ghost" aria-pressed={on} onClick={() => toggle(l)} className={cn(chip, 'h-auto font-normal', on ? 'bg-surface-4 text-fg hover:bg-hover hover:text-white' : 'bg-surface-3 text-fg-muted hover:bg-hover hover:text-fg')}>{LEVEL[l]}</SButton>
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="primary" disabled={save.isPending} onClick={() => save.mutate(CHECK_LEVELS.filter((l) => draft.includes(l)))}>Save</Button>
            {t.levelsByPerson && <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate(null)}>Let Claude pick</Button>}
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            {w.stage === 'pr_open' && <span className="text-2xs text-fg-faint">Saving runs the checks again on the pull request.</span>}
          </div>
          {save.error && <p className="mt-1 text-2xs text-err">{save.error.message}</p>}
        </div>
      )}
      {save.data?.recheckJobId && <p className="mt-1 text-2xs text-ok">Saved. The checks are running again for these levels.</p>}
    </section>
  )
}

/** What triage found when the ticket came in: type, estimate, area, scope, check levels. */
function TriageTags({ t }: { t?: TriageBrief | null }) {
  if (!t) return null
  const tag = 'rounded-sm bg-surface-3 px-1.5 py-px'
  return (
    <div className="mt-1.5 flex items-center gap-1 overflow-hidden text-[11px] whitespace-nowrap text-fg-muted">
      <span className={cn(tag, t.kind === 'bug' || t.kind === 'error' ? 'text-err' : '')}>{KIND[t.kind]}</span>
      {t.estimateHours != null && t.estimateHours > 0 && <span className={tag} style={{ color: heat(t.estimateHours) }}>{duration(t.estimateHours)}</span>}
      {t.area && <span className={cn(tag, 'max-w-32 truncate')}>{t.area}</span>}
      {t.scope === 'in' && <span className={cn(tag, 'text-ok')}>In scope</span>}
      {t.scope === 'out' && <span className={cn(tag, 'text-warn')}>{t.held ? 'Out of scope · held' : 'Out of scope'}</span>}
      {(t.levels ?? []).map((l) => <span key={l} className={cn(tag, l === 'money' && 'bg-warn/15 font-semibold text-warn')} title={t.levelsByPerson ? 'Set by hand' : undefined}>{LEVEL[l]}</span>)}
    </div>
  )
}

type Filter = { q: string; kind: string; size: string; area: string; scope: string; level: string }
const NO_FILTER: Filter = { q: '', kind: '', size: '', area: '', scope: '', level: '' }

function matches(f: Filter, ticket: string, title: string, t?: TriageBrief | null) {
  const q = f.q.trim().toLowerCase()
  if (q && !ticket.toLowerCase().includes(q) && !title.toLowerCase().includes(q)) return false
  if (!f.kind && !f.size && !f.area && !f.scope && !f.level) return true
  if (!t) return false
  return (!f.kind || t.kind === f.kind) && (!f.size || t.size === f.size)
    && (!f.area || areaKey(t.area) === f.area) && (!f.scope || t.scope === f.scope)
    && (!f.level || (t.levels ?? []).includes(f.level as CheckLevel))
}

function FilterBar({ f, set, areas }: { f: Filter; set: (f: Filter) => void; areas: string[] }) {
  const pick = (k: keyof Filter, label: string, opts: [string, string][]) => (
    <Listbox aria-label={label} className="w-36" value={f[k]} search={opts.length > 12} onChange={(v) => set({ ...f, [k]: v })}
      options={[{ value: '', label }, ...opts.map(([value, l]) => ({ value, label: l }))]} />
  )
  const any = f !== NO_FILTER && Object.values(f).some(Boolean)
  return (
    <div className="flex flex-wrap items-center gap-2 px-1 pb-1">
      <div className="relative min-w-48 flex-1">
        <Search size={13} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-faint" />
        <Input aria-label="Search tickets" className="h-8 w-full pl-8" placeholder="Search tickets" value={f.q} onChange={(e) => set({ ...f, q: e.target.value })} />
      </div>
      {pick('kind', 'Any type', Object.entries(KIND))}
      {pick('size', 'Any size', [['S', 'S'], ['M', 'M'], ['L', 'L']])}
      {pick('area', 'Any area', areas.map((a) => [a, a]))}
      {pick('scope', 'Any scope', [['in', 'In scope'], ['out', 'Out of scope']])}
      {pick('level', 'Any check', Object.entries(LEVEL))}
      {any && <Button className="h-8" onClick={() => set(NO_FILTER)}>Clear</Button>}
    </div>
  )
}

/** True when a click landed on a button, link or field inside a card, which handles it itself. */
const onControl = (t: EventTarget) => t instanceof Element && !!t.closest('button, a, input, textarea, select, label')

/** A ticket waiting in the incoming Linear state with no workspace yet: no panel to open, so it links to Linear. */
function NewCard({ t, children }: { t: ReadyTicket; children?: React.ReactNode }) {
  return (
    <li onClick={(e) => { if (!onControl(e.target)) window.open(t.url, '_blank', 'noopener,noreferrer') }} className="flex min-h-32 cursor-pointer flex-col rounded-md bg-surface-2 p-2.5 hover:bg-hover">
      <a href={t.url} target="_blank" rel="noopener noreferrer" className="block hover:text-accent-hover">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-fg-subtle">{t.ticket}</span>
          <ExternalLink size={11} className="text-fg-faint" />
          <SizeTag t={t.triage} />
          {t.updatedAt > 0 && <Age from={t.updatedAt} className="ml-auto text-fg-faint" />}
        </div>
        <div className="mt-1 truncate text-sm">{t.title}</div>
      </a>
      <TriageTags t={t.triage} />
      {children && <div className="mt-auto">{children}</div>}
    </li>
  )
}

function NeedsBits({ project, w, n }: { project: string; w: WorkspaceSummary; n?: Needs }) {
  const line = n?.needed
  return (
    <>
      {line && <p className="mt-1 truncate text-xs text-fg-muted" title={line}>{n?.asks && <span className="font-medium text-accent">Question: </span>}{line}</p>}
      {!!n?.pictures && <p className="mt-1 inline-flex items-center gap-1 text-2xs text-accent"><ImageIcon size={11} /> {n.pictures === 1 ? '1 picture' : `${n.pictures} pictures`} to look at</p>}
      <Actions project={project} w={w} lastJobId={n?.lastJobId ?? null} asks={n?.asks} small />
    </>
  )
}

/**
 * On the card (small) and in the panel. A question leads with Answer (and its
 * options, one click each); a stop leads with Try again.
 */
function Actions({ project, w, lastJobId, small, asks, options = [] }: { project: string; w: WorkspaceSummary; lastJobId: string | null; small?: boolean; asks?: boolean; options?: string[] }) {
  const act = useActions(project)
  const [text, setText] = useState('')
  const [replying, setReplying] = useState(false)
  const err = act.reply.error ?? act.retry.error
  const sz = small ? 'h-6 px-2 text-2xs' : ''
  return (
    <div className="mt-2">
      {replying && <Textarea rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Your answer; Claude picks up from here." />}
      {err && <div className="mt-2"><ErrorState error={err} compact /></div>}
      <div className={cn('flex items-center gap-1.5', small ? 'flex-nowrap' : 'flex-wrap', replying && 'mt-2')}>
        {replying
          ? <>
              <Button className={sz} variant="primary" disabled={!text.trim() || act.reply.isPending}
                onClick={() => act.reply.mutate({ workspaceId: w.id, text }, { onSuccess: () => { setText(''); setReplying(false) } })}>Send</Button>
              <Button className={sz} onClick={() => setReplying(false)}>Cancel</Button>
            </>
          : asks
            ? <>
                {!small && options.map((o) => (
                  <Button key={o} className={sz} disabled={act.reply.isPending || act.reply.isSuccess} onClick={() => act.reply.mutate({ workspaceId: w.id, text: o })}>{o}</Button>
                ))}
                <Button className={sz} variant="primary" disabled={act.reply.isSuccess} onClick={() => setReplying(true)}>{act.reply.isSuccess ? 'Sent' : options.length && !small ? 'Write an answer' : 'Answer'}</Button>
              </>
            : <>
                <Button className={sz} variant="primary" disabled={!lastJobId || act.retry.isPending} onClick={() => lastJobId && act.retry.mutate(lastJobId)}>{act.retry.isSuccess ? 'Queued' : 'Try again'}</Button>
                <Button className={sz} onClick={() => setReplying(true)}>Tell Claude what to do</Button>
              </>}
      </div>
    </div>
  )
}

/** The ticket in plain words, beside the list (over it on narrow screens). Esc or X closes. */
export function Panel({ project, w, n, onClose, embedded, lastJobId }: { project: string; w: WorkspaceSummary; n?: Needs; onClose?: () => void; embedded?: boolean; lastJobId?: string | null }) {
  const jobId = lastJobId ?? n?.lastJobId ?? null
  const story = useStory(w.id)
  const visuals = useVisuals(w.id).data?.visuals ?? []
  const asked = visuals.filter((v) => v.forQuestion)
  const others = visuals.filter((v) => !v.forQuestion)
  // Newest run first (the API's order): its pictures show; earlier runs fold away, one gallery each.
  const runs = others.reduce<Visual[][]>((acc, v) => { const last = acc.at(-1); if (last?.[0]?.jobId === v.jobId) last.push(v); else acc.push([v]); return acc }, [])
  const latest = asked.length ? [] : runs[0] ?? []
  const earlier = asked.length ? runs : runs.slice(1)
  const act = useActions(project)
  const [picked, setPicked] = useState<string | null>(null)
  const proj = useProject(project).data
  const pr = w.prNumber != null ? prLink(proj, w.prNumber) : undefined
  const [olderOpen, setOlderOpen] = useState<Record<number, boolean>>({})
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onClose])
  const s = story.data
  // From the story, not w.stage: right after an answer the workspace can lag a beat behind.
  const waiting = s?.waitingOn === 'stuck'

  return (
    <aside aria-label={`${w.ticket} details`} className={embedded ? 'flex h-full flex-col overflow-auto' : 'absolute inset-0 z-10 flex flex-col overflow-auto bg-surface-1 md:static md:w-[26rem] md:shrink-0 md:bg-surface-2/40'}>
      <div className="flex items-start gap-2 px-4 pt-4">
        <div className="min-w-0 flex-1">
          {!embedded && <h2 className="text-base font-semibold">{w.title}</h2>}
          <div className={cn('flex flex-wrap items-center gap-3 text-xs text-fg-subtle', !embedded && 'mt-1')}>
            {!embedded && <span className="font-mono">{w.ticket}</span>}
            {!embedded && <a href={w.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">Open in Linear <ExternalLink size={11} /></a>}
            {s?.slackUrl && <a href={s.slackUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">Slack conversation <ExternalLink size={11} /></a>}
          </div>
          <p className="mt-2 flex items-center gap-2 text-sm"><StatusDot stage={w.stage} printing={isPrinting(w)} />{s?.status ?? '…'}</p>
        </div>
        {onClose && <SButton variant="ghost" size="icon-xs" onClick={onClose} aria-label="Close" className="text-fg-muted hover:bg-hover hover:text-fg"><X size={14} /></SButton>}
      </div>
      <div className="space-y-5 p-4">
        {story.error && !s ? <ErrorState error={story.error} compact retry={() => void story.refetch()} /> : !s ? <SkeletonRows rows={5} height={16} /> : (
          <>
            {s.waitingOn === 'question' && s.question && (
              <div className="rounded-md bg-accent/10 p-3">
                <h3 className="text-xs font-semibold text-accent">Claude asks</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm">{s.question.text}</p>
                {s.question.context && <p className="mt-2 whitespace-pre-wrap text-xs text-fg-muted">{s.question.context}</p>}
                {asked.length > 0 && (
                  <VisualGallery
                    visuals={asked}
                    compact
                    className="mt-3"
                    answering={{
                      options: s.question.options,
                      picked,
                      disabled: act.reply.isPending || act.reply.isSuccess,
                      pick: (o) => void ask({ title: 'Send this answer to Claude?', body: o, confirm: 'Send' }).then((ok) => ok && (setPicked(o), act.reply.mutate({ workspaceId: w.id, text: o, picked: true }))),
                    }}
                  />
                )}
                {act.reply.isSuccess && <p className="mt-2 text-xs text-ok">Sent. Claude carries on with “{picked}”.</p>}
                <Actions project={project} w={w} lastJobId={jobId} asks options={s.question.options} />
                <p className="mt-2 text-2xs text-fg-subtle">You can also answer on the Linear ticket{s.slackUrl ? ' or in the Slack thread' : ''}.</p>
              </div>
            )}
            {act.reply.isSuccess && picked && s.waitingOn !== 'question' && (
              <div className="rounded-md bg-ok/10 p-3">
                <h3 className="text-xs font-semibold text-ok">Answer sent</h3>
                <p className="mt-1 text-sm">Claude carries on with “{picked}”.</p>
              </div>
            )}
            {s.waitingOn === 'review' && w.prNumber != null && (
              <div className="rounded-md bg-ok/10 p-3">
                <h3 className="text-xs font-semibold text-ok">Ready for review</h3>
                <p className="mt-1 text-sm">Pull request #{w.prNumber} is open on GitHub.</p>
              </div>
            )}
            {waiting && s.waitingOn !== 'question' && (
              <div className="rounded-md bg-warn/10 p-3">
                <h3 className="text-xs font-semibold text-warn">{w.stage === 'paused' ? 'Paused' : 'Why it stopped'}</h3>
                <p className="mt-1 text-sm">{s.needed ?? 'Claude stopped without saying why. Open Technical details, or try again.'}</p>
                <Actions project={project} w={w} lastJobId={jobId} />
                {s.details && (
                  <details className="mt-3 text-xs text-fg-subtle">
                    <summary className="cursor-pointer select-none">Technical details</summary>
                    <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap font-mono text-[11px]">{s.details}</pre>
                  </details>
                )}
              </div>
            )}
            <VisualGallery visuals={latest} compact title="Pictures Claude made" />
            <LevelsEditor project={project} w={w} />
            {earlier.length > 0 && (
              <details className="group">
                <summary className="cursor-pointer list-none text-xs font-semibold text-fg-subtle hover:text-fg">
                  <ChevronRight size={12} className="mr-1 inline transition group-open:rotate-90" />Earlier pictures ({earlier.flat().length}, from {earlier.length === 1 ? 'an earlier run' : `${earlier.length} earlier runs`})
                </summary>
                <div className="mt-2 space-y-4">{earlier.map((r) => <VisualGallery key={r[0]!.jobId} visuals={r} compact />)}</div>
              </details>
            )}
            {(w.prNumber != null || ['needs_human', 'pr_pending', 'implementing'].includes(w.stage)) && <Changes workspaceId={w.id} prUrl={pr} />}
            <div>
              <h3 className="mb-2 text-xs font-semibold text-fg-subtle">What happened</h3>
              {s.attempts.length === 0 ? <p className="text-xs text-fg-faint">Nothing yet.</p> : s.attempts.map((a, i) => {
                const shown = i === 0 || olderOpen[a.n]
                return (
                  <div key={a.n} className="mb-2">
                    <SButton variant="ghost" onClick={() => i > 0 && setOlderOpen((o) => ({ ...o, [a.n]: !o[a.n] }))} className="h-auto w-full justify-start gap-1 p-0 text-xs font-normal text-fg-muted hover:bg-transparent">
                      {i > 0 && (shown ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                      Attempt {a.n} · <Age from={a.at} />
                    </SButton>
                    {shown && (
                      <ol className="mt-1 space-y-1 pl-4">
                        {[...a.steps].reverse().map((st, j) => (
                          <li key={j} className={cn('text-sm', st.bad ? 'text-err' : 'text-fg')}>{st.label}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </aside>
  )
}
